/**
 * The verdict for "may this pot go into this spot on this day?". It's what the scanner shows,
 * what the planner ranks by, and what an edited plan is checked against: one function, one
 * set of rules.
 *
 *   red    the 12-month promise would be broken, or the pot is unknown or retired
 *   amber  allowed, but look first: history incomplete, a client requirement not met,
 *          or the records say this pot is somewhere else
 *   green  nothing to report
 */
import type { CalendarDate } from "./calendar";
import type { Catalog, Pot, Requirement } from "./catalog";
import type { History } from "./history";
import type { WriteOffReason } from "./events";
import { checkRepeatRule, type RepeatPolicy } from "./repeat-rule";

export type Light = "green" | "amber" | "red";

export type Reason =
  | { readonly code: "unknown-pot"; readonly light: "red" }
  | { readonly code: "pot-retired"; readonly light: "red" }
  | { readonly code: "pot-written-off"; readonly light: "red"; readonly on: CalendarDate; readonly reason: WriteOffReason }
  | { readonly code: "repeat-blocked"; readonly light: "red"; readonly lastPlacedOn: CalendarDate; readonly allowedFrom: CalendarDate }
  | { readonly code: "history-unverified"; readonly light: "amber"; readonly historyKnownSince: CalendarDate }
  | {
      readonly code: "requirement-not-met";
      readonly light: "amber";
      readonly level: "client" | "spot";
      readonly requirement: Requirement;
      /** What the pot actually has for that attribute, for the message ("this pot is pink"). */
      readonly potValues: readonly string[];
    }
  | {
      readonly code: "recorded-elsewhere";
      readonly light: "amber";
      readonly clientId: string;
      readonly slotId: string;
      readonly placedOn: CalendarDate;
    };

export interface Verdict {
  readonly light: Light;
  /** Most serious first. Empty when green. */
  readonly reasons: readonly Reason[];
}

export interface Assessor {
  assess(potId: string, slotId: string, on: CalendarDate): Verdict;
}

export interface AssessorInput {
  readonly catalog: Catalog;
  readonly history: History;
  readonly policy: RepeatPolicy;
  /** First day from which this tenant's records are complete. */
  readonly historyKnownSince: CalendarDate;
}

const normalise = (value: string): string => value.trim().toLowerCase();

function missedRequirement(pot: Pot, requirement: Requirement): readonly string[] | null {
  const potValues = (pot.attributes[requirement.attribute] ?? []).map(normalise);
  const listed = new Set(requirement.values.map(normalise));
  const met =
    requirement.rule === "only"
      ? potValues.length > 0 && potValues.every((v) => listed.has(v))
      : potValues.every((v) => !listed.has(v));
  return met ? null : potValues;
}

const SEVERITY: Record<Light, number> = { red: 2, amber: 1, green: 0 };

function toVerdict(reasons: Reason[]): Verdict {
  reasons.sort((a, b) => SEVERITY[b.light] - SEVERITY[a.light]);
  return { light: reasons[0]?.light ?? "green", reasons };
}

export function createAssessor({ catalog, history, policy, historyKnownSince }: AssessorInput): Assessor {
  return {
    assess(potId, slotId, on) {
      const pot = catalog.pot(potId);
      if (!pot) return { light: "red", reasons: [{ code: "unknown-pot", light: "red" }] };

      const slot = catalog.slot(slotId);
      const reasons: Reason[] = [];
      if (!pot.active) reasons.push({ code: "pot-retired", light: "red" });
      const writeOff = history.writtenOff(potId);
      if (writeOff) reasons.push({ code: "pot-written-off", light: "red", on: writeOff.on, reason: writeOff.reason });

      const repeat = checkRepeatRule({ potId, clientId: slot.clientId, slotId, on }, history, policy, historyKnownSince);
      if (repeat.status === "blocked") {
        reasons.push({ code: "repeat-blocked", light: "red", lastPlacedOn: repeat.lastPlacedOn, allowedFrom: repeat.allowedFrom });
      } else if (repeat.status === "unverified") {
        reasons.push({ code: "history-unverified", light: "amber", historyKnownSince: repeat.historyKnownSince });
      }

      const levels: [level: "client" | "spot", requirements: readonly Requirement[]][] = [
        ["client", catalog.client(slot.clientId)?.requirements ?? []],
        ["spot", slot.requirements],
      ];
      for (const [level, requirements] of levels) {
        for (const requirement of requirements) {
          const potValues = missedRequirement(pot, requirement);
          if (potValues) reasons.push({ code: "requirement-not-met", light: "amber", level, requirement, potValues });
        }
      }

      const current = history.whereIs(potId);
      if (current && current.slotId !== slotId) {
        reasons.push({
          code: "recorded-elsewhere",
          light: "amber",
          clientId: current.clientId,
          slotId: current.slotId,
          placedOn: current.placedOn,
        });
      }

      return toVerdict(reasons);
    },
  };
}
